import React, { useContext } from "react";
import { useStyles } from "./GameSessionStyles.css";
import useGameSession from "./useGameSession";
import { AppbarContentContext } from "components/commons/routing/appbar/AppbarContentProvider";
import DrawingSection from "components/admin/sections/videosScored/drawing/drawingDefinition/DrawingSection";
import ScoreboardTopReview from "../scoreboard/top/ScoreboardTopReview";
import ScoreboardBottomButtons from "../scoreboard/bottom/ScoreboardBottomButtons";
import ScoreboardBottomReviewRunningTime from "../scoreboard/bottom/ScoreboardBottomReviewRunningTime";
import ScoreboardTopPoints from "../scoreboard/top/ScoreboardTopPoints";
import TrailingPeriodScreen from "../overlay/overtime/TrailingPeriodScreen";
import LogoOverlay from "../overlay/logo/LogoOverlay";
/*
    Parent component for all the different game session components
    Based on the discipline, a different game session will be loaded, with its corresponding
    scoreboards and point systems.
 */

const GameSessionRunningTime = ({ currentVideo, playlistInfo }) => {
    const classes = useStyles();

    const { setCurrentAppbarContent } = useContext(AppbarContentContext);

    const {
        // Session related states

        pointScoreState,
        penaltyRedScoreState,
        penaltyBlueScoreState,
        activeStep,
        isOvertime,
        disabled,
        videoEnd,
        reviewEnd,
        loadingFeedback,
        feedbackResult,
        videoPlaying,
        overtimeDuration,

        // Methods and other useful stuff

        decisionHandlers,
        handleClickPlay,
        handleReplayScene,
        handleChangeActiveDecision,
        checkRingOrTatami,
        disableOnClick,
        handleEndOvertime,

        // Highlighting

        activeDrawingScoreDefinition,
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop,
        videoHeight,
        videoWidth,
        showBlurring,
    } = useGameSession(currentVideo);

    const sessionInfo = {
        courseId: currentVideo.courseId,
        playlistId: currentVideo.playlistId,
        scoredSceneId: currentVideo.key,
        playlistName: playlistInfo.playlistName,
        scoredSceneCount: playlistInfo.scoredSceneCount,
        currentIndex: playlistInfo.currentScene,
        isReplay: currentVideo.isReplay
    };

    return (
        <div className={classes.gameSessionContainer}>
            {/*
                Upper bar of the game screen, where points and penalties are displayed during the game
                Based on the type of game session we load (ring, kumite, tatami) we will load different
                scores and buttons.
            */}

            <div>
                {videoEnd === false && (
                    <ScoreboardTopPoints
                        discipline={currentVideo.discipline}
                        association={currentVideo.association}
                        ageGroup={currentVideo.ageGroup}
                        gender={currentVideo.gender}
                        scores={pointScoreState}
                        penaltyRed={penaltyRedScoreState}
                        penaltyBlue={penaltyBlueScoreState}
                        athleteLeftPosition={currentVideo.athleteLeftPosition}
                    />
                )}
                {videoEnd === true &&
                    reviewEnd === false &&
                    loadingFeedback === false && (
                        <ScoreboardTopReview
                            matchedDecisions={feedbackResult.matchedArray}
                            activeStep={activeStep}
                            sessionInfo={sessionInfo}
                            feedbackMode={currentVideo.feedbackMode}
                            sessionType={"running_time"}
                            loading={loadingFeedback}
                        />
                    )}
            </div>

            <div id="player-wrapper" style={{ position: "relative", width: "100%" }}>
                <div
                    id="player-iframe-container"
                    style={{
                        position: "relative",
                        paddingTop: `${(videoHeight / videoWidth) * 100}%`,
                    }}
                >
                    <iframe
                        title="player-frame"
                        id="video-player"
                        width="100%"
                        height="100%"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                        }}
                        src={currentVideo.id}
                        frameBorder="0"
                        allowFullScreen=""
                        allow="autoplay"
                    />
                </div>
                {isOvertime && (
                    <TrailingPeriodScreen
                        duration={overtimeDuration}
                        handleEndOvertime={handleEndOvertime}
                    ></TrailingPeriodScreen>
                )}
                {reviewEnd !== true && !isOvertime && (
                    <div>
                        <DrawingSection
                            initDrawing={
                                activeDrawingScoreDefinition &&
                                currentVideo.scoreDefinitions[activeStep].highlighting
                            }
                            canvasTop={canvasTop}
                            canvasLeft={canvasLeft}
                            canvasHeight={canvasHeight}
                            canvasWidth={canvasWidth}
                            scoredSceneStartTime={currentVideo.sceneStart}
                            scoredSceneEndTime={currentVideo.sceneEnd}
                            drawingMode={false}
                            previewMode={true}
                            mode="highlight"
                        />
                    </div>
                )}

                {videoPlaying === false && (
                    <LogoOverlay onButtonClick={handleClickPlay} isLoading={!disabled} />
                )}
            </div>

            {videoEnd === false ? (
                <ScoreboardBottomButtons
                    points={currentVideo.sessionProperties.sessionPoints}
                    penalties={currentVideo.sessionProperties.sessionPenalties}
                    athleteLeft={currentVideo.athleteLeftPosition}
                    decisionHandlers={decisionHandlers}
                    disabled={disabled}
                />
            ) : (
                <ScoreboardBottomReviewRunningTime
                    handleStep={handleReplayScene}
                    decisions={currentVideo.scoreDefinitions}
                    activeStep={activeStep}
                    type={currentVideo.feedbackMode}
                    reviewEnd={reviewEnd}
                    sessionInfo={sessionInfo}
                    feedbackMode={currentVideo.feedbackMode}
                    endRun={currentVideo.endRun}
                />
            )}
        </div>
    );
};

export default GameSessionRunningTime;
